<template>
  <!-- The segment inside the Maps pill: newest chips, the total, and a caret. Opens the tray;
       it never clears anything by itself. -->
  <button
    type="button"
    class="otm-segment"
    :class="{ 'is-open': open }"
    aria-haspopup="dialog"
    :aria-expanded="open ? 'true' : 'false'"
    :aria-label="label"
    @click="$emit('toggle')"
  >
    <span class="otm-chips" aria-hidden="true">
      <span
        v-for="(chip, index) in chips"
        :key="chip.id"
        :class="['otm-chip', chip.shape === 'circle' ? 'otm-chip--marker' : 'otm-chip--layer']"
        :style="{ backgroundColor: chip.fill, zIndex: chips.length - index }"
      >
        <img v-if="chip.image" :src="chip.image" alt="" class="otm-chip-marker" draggable="false" />
        <svg v-else-if="chip.svgPath" class="otm-chip-svg" viewBox="0 0 24 24" :fill="chip.glyphColor">
          <path :d="chip.svgPath" />
        </svg>
        <span v-else class="material-icons otm-chip-glyph" :style="{ color: chip.glyphColor }">{{ chip.symbol }}</span>
      </span>
    </span>
    <span class="otm-count" aria-hidden="true">{{ count }}</span>
    <span class="material-icons otm-caret" aria-hidden="true">{{ open ? 'expand_less' : 'expand_more' }}</span>
  </button>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export interface OnThisMapChip {
  id: string;
  shape: 'circle' | 'square';
  fill: string;
  glyphColor?: string;
  symbol?: string;
  svgPath?: string;
  image?: string;
}

export default defineComponent({
  name: 'OnThisMapSegment',
  props: {
    chips: { type: Array as PropType<OnThisMapChip[]>, required: true },
    count: { type: Number, required: true },
    open: { type: Boolean, default: false },
    label: { type: String, required: true },
  },
  emits: ['toggle'],
});
</script>

<style scoped>
.otm-segment {
  position: relative;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 4px 4px 4px 0;
  padding: 0 4px 0 4px;
  min-width: 44px;
  border: 0;
  border-radius: 999px;
  background: #2D2F35;
  color: #fff;
  font: inherit;
  line-height: 1;
  cursor: pointer;
}

/* The pill is shorter than a comfortable tap target; extend the hit area past the inner
   pill (invisibly) so the whole segment is at least 44×40. */
.otm-segment::before {
  content: '';
  position: absolute;
  inset: -7px -4px;
}

.otm-segment.is-open {
  background: #505259;
}

.otm-segment:focus {
  outline: none;
}

.otm-segment:focus-visible {
  outline: 2px solid #fddf00;
  outline-offset: 2px;
}

.otm-chips {
  display: inline-flex;
  align-items: center;
}

.otm-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  border: 2px solid #fff;
  overflow: hidden;
}

/* Overlap by about a third, newest on top (z-index set inline). */
.otm-chip + .otm-chip {
  margin-left: -7px;
}

/* Circle = a marker on the map; rounded square = a layer. */
.otm-chip--marker {
  border-radius: 50%;
}

.otm-chip--layer {
  border-radius: 5px;
}

/* The POI's own marker artwork, cropped to the pin head: its colour ring and icon. */
.otm-chip-marker {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.otm-chip-glyph {
  font-size: 12px;
  font-variation-settings: 'FILL' 1;
  line-height: 1;
}

.otm-chip-svg {
  width: 12px;
  height: 12px;
}

.otm-count {
  font-weight: 700;
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.otm-caret {
  font-size: 16px;
  color: #ACB0BF;
  margin-left: -2px;
}

.otm-segment.is-open .otm-caret {
  color: #fff;
}
</style>
